import { useRef, useState } from "react";
import { api } from "../api";
import { GiveUp } from "../components/GiveUp";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { haptic } from "../telegram";
import { useGame } from "../useGame";

interface View {
  role: "describer" | "guesser";
  describer: string;
  len: number;
  hasClue: boolean;
  clue?: string;
  word?: string;
  attempts: string[];
  letter?: string;
}

const COPY = {
  bad_clue: "Опис на 10-200 символів",
  clue_has_word: "Не можна використовувати саме слово (і його форми!)",
  bad_word: "Одне українське слово",
  already_guessed: "Вже пробував",
};

export function Taemne({ date }: { date?: string | undefined }) {
  const g = useGame<View>("taemne", COPY, date);
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";

  const submit = async () => {
    const v = value.trim();
    if (!v || finished || g.busy) return;
    inputRef.current?.focus();
    const next = await g.run(() => api.guess<View>("taemne", v, date));
    if (next) {
      const extra = next.extra as { right?: boolean } | undefined;
      if (extra?.right === false) {
        haptic.error();
        g.showToast("Не воно 🙃", 900);
      }
      setValue("");
    }
  };

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;
  const won = s.status === "won";
  const describer = v.role === "describer";

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${s.number}`} title="ТАЄМНЕ СЛОВО" right={date ? "" : `🔥 ${s.me.streak}`} />}>
      <div className="flex-1 overflow-y-auto px-4 pb-2">
        <div className="mt-1 text-center text-xs text-tghint">
          сьогодні загадує: <b className="text-tgtext">{v.describer}</b>
        </div>

        {describer ? (
          <div className="mx-auto mt-3 max-w-[420px] space-y-3">
            <div className="rounded-2xl bg-tgsection px-5 py-5 text-center">
              <div className="text-[11px] uppercase tracking-wide text-tghint">твоє таємне слово</div>
              <div
                className={`mt-1 break-all font-extrabold ${
                  (v.word?.length ?? 0) >= 9 ? "text-2xl tracking-[0.06em]" : "text-3xl tracking-[0.15em]"
                }`}
              >
                {v.word?.toUpperCase() ?? "?"}
              </div>
            </div>
            {v.hasClue ? (
              <div className="rounded-xl bg-ok/15 px-4 py-3 text-center text-sm">
                опис у грі ✓<div className="mt-1 text-tghint">«{v.clue}»</div>
              </div>
            ) : (
              <div className="text-center text-xs text-tghint">
                опиши слово одним повідомленням - без самого слова і його форм. решта вгадуватиме по твоєму опису
              </div>
            )}
          </div>
        ) : (
          <div className="mx-auto mt-3 max-w-[420px] space-y-3">
            <div className="rounded-2xl bg-tgsection px-5 py-4 text-center">
              <div className="text-[11px] uppercase tracking-wide text-tghint">опис</div>
              {v.hasClue ? (
                <div className="mt-1 text-[16px] font-semibold leading-snug">«{v.clue}»</div>
              ) : (
                <div className="mt-1 text-sm text-tghint">
                  {v.describer} ще не написав опис… можна вгадувати наосліп або зайти пізніше
                </div>
              )}
              <div className="mt-2 flex flex-wrap items-center justify-center gap-1">
                {Array.from({ length: v.len }, (_, i) => (
                  <span
                    key={i}
                    className={`flex items-center justify-center rounded-[4px] bg-[rgba(128,128,128,0.25)] font-bold ${
                      v.len >= 9 ? "h-6 w-5 text-xs" : "h-7 w-6 text-sm"
                    }`}
                  >
                    {i === 0 && v.letter ? v.letter : ""}
                  </span>
                ))}
              </div>
              <div className="mt-1 text-[11px] text-tghint">{v.len} літер{v.letter ? ` · починається на ${v.letter}` : ""}</div>
            </div>

            {v.attempts.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {[...v.attempts].reverse().map((a) => (
                  <span key={a} className={`rounded-full px-2.5 py-1 text-xs ${won && a === v.attempts.at(-1) ? "bg-ok/25 font-bold" : "bg-[rgba(128,128,128,0.15)]"}`}>
                    {a}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {!finished && (
        <div style={{ paddingBottom: "max(var(--tg-safe-area-inset-bottom, 0px), 10px)" }}>
          <form
            className="flex gap-2 px-4 pb-1"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <input
              ref={inputRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={describer ? "опис слова (без самого слова)" : "твоя версія слова"}
              maxLength={describer ? 200 : 40}
              autoComplete="off"
              autoCorrect={describer ? "on" : "off"}
              autoCapitalize="none"
              spellCheck={describer}
              enterKeyHint="send"
              className="h-[44px] flex-1 rounded-xl border border-[rgba(128,128,128,0.35)] bg-transparent px-4 text-[16px] text-tgtext outline-none placeholder:text-[rgba(128,128,128,0.7)] focus:border-tgbutton"
            />
            <button
              type="submit"
              disabled={g.busy || !value.trim()}
              onMouseDown={(e) => e.preventDefault()}
              className="h-[44px] rounded-xl bg-tgbutton px-5 text-[15px] font-bold text-tgbuttontext active:brightness-90 disabled:opacity-35"
            >
              {g.busy ? "…" : "→"}
            </button>
          </form>
          {!describer && v.attempts.length >= 3 && (
            <div className="flex px-4">
              <GiveUp busy={g.busy} onGiveUp={() => void g.run(() => api.giveUp<View>("taemne", date))} />
            </div>
          )}
        </div>
      )}

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={describer ? "Слово в грі!" : won ? "Вгадав!" : "Здався"}
          subtitle={
            describer
              ? "чекаємо, хто вгадає твій опис"
              : won
                ? `за ${v.attempts.length} · стрік ${s.me.streak} 🔥`
                : `це було «${v.word ?? "?"}» · стрік згорів`
          }
          scoreLabel="avg спроб"
          formatScore={(n) => `${n}`}
          onDismiss={() => g.setSheetOpen(false)}
        >
          {!describer && v.word && (
            <div className="mt-4 text-center text-lg font-bold">«{v.word}»</div>
          )}
        </ResultSheet>
      )}
    </Shell>
  );
}
